/* eslint-disable jsx-a11y/anchor-is-valid */
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
	Box,
	Card,
	IconButton,
	InputAdornment,
	LinearProgress,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { GoogleLogin as GoogleLoginButton, GoogleOAuthProvider } from '@react-oauth/google';
import { decodeToken } from 'react-jwt';
import { React, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import appStoreBadge from '../../../assets/images/app-store-badge.svg';
import googlePlayBadge from '../../../assets/images/google-play-badge.png';
import { APPLE_CLIENT_ID, APPLE_REDIRECT_URI, BASE_URLS, GOOGLE_CLIENT_ID, IS_SA } from '../../../config/appConfig';
import { AppRoutes } from '../../../constants/appRoutes';
import { InvestorRegistrationStep } from '../../../constants/investorRegistrationSteps';
import { InvestorStatus } from '../../../constants/investorStatus';
import { LocalStorageKey } from '../../../constants/localStorageKeys';
import { OpenPanelEvents } from '../../../constants/openPanelEvents';
import { OTPTypes } from '../../../constants/otpTypes';
import { Roles } from '../../../constants/roles';
import { getCurrentUserPath } from '../../../helpers/authHelper';
import { trackCustomerIo } from '../../../helpers/customerIoHelper';
import { buildLocalizeRoute } from '../../../helpers/languageHelper';
import { checkIsBorrowerOrInvestor } from '../../../helpers/loginCheckHelper';
import { MarketplaceService } from '../../../pages/investor/views/marketplace/marketplaceService';
import { Platform } from '../../../pages/registration/components/platform';
import { RegistrationNavBar } from '../../../pages/registration/components/registrationNavBar';
import { Logout, appleLogin, getUser, googleLogin, login } from '../../../services/authService';
import { LanguageService } from '../../../services/languageService';
import { SystemSettingsService } from '../../../services/systemSettingsService';
import { UserService } from '../../../services/userService';
import { ButtonWrapper } from '../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../shared/components/formUtilities/useInput';
import { TextWrapper } from '../../../shared/components/formUtilities/useTextWrapper';
import { OTPAuthenticationDialog } from '../../../shared/components/otpAuthenticationDialog';
import { RegistrationDialog } from '../components/registrationDialog';
import { UserLockOutDialog } from '../components/userLockoutDialog';
import { Toaster } from '../../../shared/components/common/toaster';
import { useDispatch, useSelector } from 'react-redux';
import { setSytemSettingsData } from '../../../redux/action';

function Login({ isLoading = false }) {
	const navigate = useNavigate();
	const systemSettingsService = new SystemSettingsService();
	const languageService = new LanguageService();
	const marketplaceService = new MarketplaceService();
	const userService = new UserService();
	const [passwordType, setPasswordType] = useState('password');
	const { t } = useTranslation();

	const [loading, setLoading] = useState(isLoading);
	const [isUserLockOut, setIsUserLockOut] = useState(false);
	const [isShowOtpScreen, setIsShowOtpScreen] = useState(false);
	const [errorCounter, setErrorCounter] = useState(100);
	const [timerId, setTimerId] = useState(null);
	const dispatch = useDispatch();
	const [isShowEmailNotFoundError, setIsShowEmailNotFoundError] =
		useState(false);
	const [loginResp, setLoginResp] = useState({});
	const [showRegDialog, setShowRegDialog] = useState(false);
	const enableGoogleSSO = useSelector(
		(state) => state.systemSettingsDataReducer.settings?.enableGoogleSSO
	);
	const showGoogleSignIn = enableGoogleSSO === true && !!GOOGLE_CLIENT_ID;
		const enableAppleSSO = useSelector(
			(state) => state.systemSettingsDataReducer.settings?.enableAppleSSO
		);
		const showAppleSignIn = enableAppleSSO === true && !!APPLE_CLIENT_ID && !!APPLE_REDIRECT_URI;

	// eslint-disable-next-line no-unused-vars
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		localStorage.removeItem('accessDenied');
		systemSettingsService
			.getAllFront()
			.then((resp) => {
				dispatch(setSytemSettingsData(resp));
			})
			.catch((resp) => { });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const returnUrl = params.get('returnUrl');
		const lang = window.location.pathname.includes('/ar/') ? 'ar' : 'en';
		if (returnUrl && returnUrl.includes(AppRoutes.INafathVerification) && !IS_SA) {
			const url = `${BASE_URLS.sa}/${lang}/login/?returnUrl=investor/profile/nafath-verification`;
			setTimeout(() => {
				window.location.href = url;
			}, 200);
		}
	}, []);

	useEffect(() => {
		if (isShowEmailNotFoundError) {
			startCountdown();
		}
	}, [isShowEmailNotFoundError]);

	const startCountdown = () => {
		setErrorCounter(100);
		const id = setInterval(() => {
			setErrorCounter((countdown) => countdown - 1);
		}, 100);
		setTimerId(id);
	};

	useEffect(() => {
		if (errorCounter <= 0) {
			setErrorCounter(100);
			setIsShowEmailNotFoundError(false);
			clearInterval(timerId);
		}
	}, [errorCounter, timerId]);

	useEffect(() => {
		return () => clearInterval(timerId);
	}, [timerId]);

	const togglePassword = () => {
		if (passwordType === 'password') {
			setPasswordType('text');
			return;
		}
		setPasswordType('password');
	};

	const isAr = () => languageService.isArabic();

	const loginSchema = yup.object({
		email: yup.string().email().required(t('required')),
		password: yup.string().required(t('required')),
	});

	const initialValues = {
		email: '',
		password: '',
	};

	const onSubmit = (values, e) => {
		setLoading(true);
		login(values)
			.then((loginResp) => {
				setLoginResp(loginResp.data);
				afterLogin(loginResp);
			})
			.catch((err) => {
				setIsUserLockOut(checkIfUserLockOut(err));
				if (err?.response?.data?.EmailNotConfirmed) {
					setTimeout(() => {
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(values.email)}&type=1`));
					}, 300);
					return;
				}
				if (
					err?.response?.data?.loginEmailNotFound &&
					!checkIfUserLockOut(err)
				) {
					setIsShowEmailNotFoundError(true);
				} else {
					setIsShowEmailNotFoundError(false);
				}

				setLoading(false);
			});
	};

	const onGoogleLoginSuccess = (credentialResponse) => {
		setLoading(true);
		googleLogin(credentialResponse.credential)
			.then((loginResp) => {
				if (!loginResp) {
					setLoading(false);
					return;
				}
				setLoginResp(loginResp.data);
				afterLogin(loginResp);
			})
			.catch((err) => {
				if (err?.response?.data?.EmailNotConfirmed) {
					const googlePayload = decodeToken(credentialResponse.credential);
					setTimeout(() => {
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(googlePayload?.email || '')}&type=1`));
					}, 300);
					return;
				}
				setLoading(false);
				if (err?.response?.data?.NoAccountFoundForGoogleEmail) {
					Toaster({ msg: t('googleNoAccountFound') });
					return;
				}
				Toaster({ msg: t('googleSignInFailed') });
			});
	};

	const onGoogleLoginError = () => {
		Toaster({ msg: t('googleSignInFailed') });
	};

	useEffect(() => {
		if (!showAppleSignIn) return;

		const initAppleSignIn = () => {
			window.AppleID.auth.init({
				clientId: APPLE_CLIENT_ID,
				scope: 'email',
				redirectURI: APPLE_REDIRECT_URI,
				state: 'fs-web',
				usePopup: true,
			});
		};

		if (window.AppleID) {
			initAppleSignIn();
		} else {
			const script = document.createElement('script');
			script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
			script.async = true;
			script.onload = initAppleSignIn;
			document.body.appendChild(script);
		}
	}, [showAppleSignIn]);

	const onAppleSignInSuccess = (event) => {
		const idToken = event.detail.authorization.id_token;
		const email = event.detail.user?.email || decodeToken(idToken)?.email || '';
		setLoading(true);
		appleLogin(idToken)
			.then((loginResp) => {
				if (!loginResp) {
					setLoading(false);
					return;
				}
				setLoginResp(loginResp.data);
				afterLogin(loginResp);
			})
			.catch((err) => {
				setLoading(false);
				if (err?.response?.data?.EmailNotConfirmed) {
					setTimeout(() => {
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(email)}&type=1`));
					}, 300);
					return;
				}
				if (err?.response?.data?.AppleEmailHiddenCode) {
					Toaster({ msg: t('appleEmailHidden') });
					return;
				}
				if (err?.response?.data?.AppleEmailNotSharedCode) {
					Toaster({ msg: t('appleEmailNotShared') });
					return;
				}
				if (err?.response?.data?.NoAccountFoundForAppleEmail) {
					Toaster({ msg: t('appleNoAccountFound') });
					return;
				}
				Toaster({ msg: t('appleSignInFailed') });
			});
	};

	const onAppleSignInFailure = (event) => {
		const error = event.detail?.error;
		if (error === 'popup_closed_by_user' || error === 'user_cancelled_authorize') {
			return;
		}
		Toaster({ msg: t('appleSignInFailed') });
	};

	const onAppleSignIn = () => {
		window.AppleID.auth
			.signIn()
			.then((response) => onAppleSignInSuccess({ detail: response }))
			.catch((err) => onAppleSignInFailure({ detail: err }));
	};

	const getHasMarketplace = async () => {
		try {
			setLoading(true);
			const resp = await marketplaceService.getMarketplace();
			const isMarketplace = resp.data && resp.data.length > 0;
			setLoading(false);
			return isMarketplace;
		}
		catch {
			setLoading(false);
			return false;
		}
	};

	const afterLogin = (loginResponse) => {
		if (loginResponse.data?.otpRequested) {
			setTimeout(() => {
				showOtpVerification();
			});
		} else {
			setTimeout(() => {
				afterLoginSuccess(loginResponse.data);
			});
		}
	};

	const showOtpVerification = () => {
		setIsShowOtpScreen(true);
	};

	const afterLoginSuccess = async (data = null) => {
		const user = getUser();
		const isSA = IS_SA;
		let loginData = data ? data : loginResp;
		let isMarketplace = false;
		const isInvestor = loginData?.roles?.find(role => role === Roles.Investor);

		const checkPointData = await userService.getCheckPoint();
		if (checkPointData) {
			localStorage.setItem(LocalStorageKey.checkPointServey, JSON.stringify(checkPointData.data));
		}

		if (loginData.mustResetPassword) {
			navigate(buildLocalizeRoute(AppRoutes.PasswordExpired));
			return;
		}

		if (user?.status === InvestorStatus.Archived && !user?.isApproved && isInvestor) {
			let url = `${AppRoutes.Investor}/${AppRoutes.ThankYou}`;
			navigate(buildLocalizeRoute(url));
			return;
		}

		if(isSA && isInvestor && loginData.showNafathPage) {
			let url = `${AppRoutes.Investor}/${AppRoutes.Profile}/${AppRoutes.INafathVerification}`;
			navigate(buildLocalizeRoute(url));
			return;
		}

		if (isInvestor && loginData?.investorStep >= InvestorRegistrationStep.Declaration) {
			isMarketplace = await getHasMarketplace();
		}


		let route = checkIsBorrowerOrInvestor(loginData, user);
		if (route) {

			if (route === AppRoutes.Investor && isMarketplace) {
				navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Marketplace}`));
				return;
			}

			navigate(buildLocalizeRoute(route));
			return;
		}

		if (!user?.isApproved) {
			if (user?.status === InvestorStatus.WindDown && !isInvestor) {
				navigate(buildLocalizeRoute(AppRoutes.WindDown));
				return;
			}
			redirectToThankYouPage(loginData?.roles, isMarketplace);
		} else {
			onNavigateToDashboard(isMarketplace);
		}
	};

	const redirectToThankYouPage = (roles = [], isMarketplace = false) => {
		if (roles.find((resp) => resp === Roles.Investor)) {
			onNavigateToDashboard(isMarketplace);
			return;
		}
		let url = `${AppRoutes.Borrower}/${AppRoutes.ThankYou}`;
		navigate(buildLocalizeRoute(url));
		return;
	};

	const onNavigateToDashboard = async (isMarketplace = false) => {
		const returnUrl = searchParams.get('returnUrl');
		let route = getCurrentUserPath(returnUrl);
		if (route === AppRoutes.Investor && isMarketplace) {
			navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Marketplace}`));
			return;
		}
		navigate(route);
	};

	const checkIfUserLockOut = (err) => {
		let response = err?.response || null;
		if (response && response.status === 400 && response?.data?.UserLockedOut) {
			return true;
		}

		return false;
	};

	const getLoginUrl = () => {
		let baseURL = !IS_SA ? BASE_URLS.sa : BASE_URLS.en;
		return `${baseURL}/${languageService.getCurrentLanguage()}/login`;
	};

	return (
		<>
			<RegistrationNavBar isLogin={true}></RegistrationNavBar>
			<div className="flex flex-auto relative flex-col items-center justify-center min-w-0 md:h-[calc(100%-130px)] auth-card-wrapper bg-white p-3 md:p-0">
				<Box
					component={Card}
					className="flex md:flex-row flex-col !rounded-lg h-auto overflow-auto w-full md:w-auto">
					<Box className="flex relative md:flex-auto md:h-auto h-full flex-col md:py-12 md:px-12 p-6 bg-white md:w-[400px] w-full">
						<Box
							className={`absolute ${isAr() ? 'left-[24px]' : 'right-[24px]'
								}   top-[24px]`}>
							<Platform />
						</Box>
						<TextWrapper
							classList="!text-[24px] font-bold !text-center !mt-[34px] md:!mt-[22px] !mb-1"
							type="h6"
							text={t('welcomeBack')}
						/>
						<TextWrapper
							classList="!text-[15px] !caption !text-center !mb-2"
							type="h6"
							text={t('signInToYourAccount')}
						/>
						<Formik
							initialValues={initialValues}
							onSubmit={onSubmit}
							validationSchema={loginSchema}
							validateOnMount={true}
							enableReinitialize={true}>
							{(formik) => {
								return (
									<Form className="mt-3">
										<Box className="grid grid-rows-2 gap-6">
											<InputWrapper
												errorStateTestID="emailError"
												key={'email'}
												label={'email'}
												size="small"
												error={formik.errors.email && formik.touched.email}
												helperText={
													formik.errors.email && formik.touched.email
														? t(formik.errors.email)
														: ''
												}
												variant="outlined"
												fullWidth={true}
												type={'email'}
												name="email"></InputWrapper>
											<InputWrapper
												errorStateTestID="passwordError"
												error={
													formik.errors.password && formik.touched.password
												}
												helperText={
													formik.errors.password && formik.touched.password
														? t(formik.errors.password)
														: ''
												}
												fullWidth={true}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																className="!bg-transparent text-primary"
																onClick={togglePassword}>
																{passwordType === 'password' ? (
																	<VisibilityOffOutlinedIcon></VisibilityOffOutlinedIcon>
																) : (
																	<VisibilityOutlinedIcon></VisibilityOutlinedIcon>
																)}
															</IconButton>
														</InputAdornment>
													),
												}}
												key={'password'}
												label={'password'}
												variant="outlined"
												type={passwordType}
												placeholder={t('enterPassword')}
												name="password"
												size="small"
											/>
										</Box>
										{isShowEmailNotFoundError ? (
											<Box className="mt-6">
												<LinearProgress
													color="error"
													variant="determinate"
													value={errorCounter}
												/>
												<Box className="text-[#d32f2f] !text-[0.75rem] mt-1">
													<span>
														<span>{t('loginEmailNotFound')}</span>
														&nbsp;
														<a
															className="!text-[0.75rem] !text-[#d32f2f] underline"
															href={getLoginUrl()}>
															{t('here')}
														</a>
													</span>
												</Box>
											</Box>
										) : null}
										<ButtonWrapper
											dataTestID="signInBtn"
											icon={<LockOpenOutlinedIcon />}
											loading={loading}
											label={t('signIn')}
											classList="w-full !mt-6"
											type="submit"></ButtonWrapper>
										{showGoogleSignIn ? (
											<>
												<Box className="flex items-center mt-4">
													<Box className="flex-1 border-t border-gray-200" />
													<TextWrapper
														classList="!text-[12px] !caption !mx-3"
														type="caption"
														text={t('or')}
													/>
													<Box className="flex-1 border-t border-gray-200" />
												</Box>
												<Box className="flex justify-center mt-4" data-testid="googleSignInBtn">
													<GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
														<div style={{ position: 'relative', width: 300, height: 40 }}>
															<button type="button" tabIndex={-1} aria-hidden="true" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 40, border: '1px solid #dadce0', borderRadius: 4, background: '#fff', color: '#3c4043', fontFamily: 'Roboto, arial, sans-serif', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
																<svg style={{ position: 'absolute', left: 9, top: 11, width: 18, height: 18 }} viewBox="0 0 48 48" aria-hidden="true">
																	<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
																	<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
																	<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
																	<path fill="#34A853" d="M24 48c6.48 0 11.930-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
																</svg>
																Sign in with Google
															</button>
															<div style={{ position: 'absolute', inset: 0, opacity: 0, overflow: 'hidden' }}>
																<GoogleLoginButton
																	onSuccess={onGoogleLoginSuccess}
																	onError={onGoogleLoginError}
																	width={300}
																	text="signin_with"
																/>
															</div>
														</div>
													</GoogleOAuthProvider>
												</Box>
											</>
										) : null}
											{showAppleSignIn ? (
												<Box className="flex justify-center mt-4" data-testid="appleSignInBtn">
													<button type="button" onClick={onAppleSignIn} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 40, border: '1px solid #dadce0', borderRadius: 4, background: '#fff', color: '#3c4043', fontFamily: 'Roboto, arial, sans-serif', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}><svg style={{ position: 'absolute', left: 9, top: 11, width: 18, height: 18 }} viewBox="0 0 24 24" aria-hidden="true"><path fill="#000000" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>Sign in with Apple</button>
												</Box>
											) : null}
											<Box className="text-center mt-6">
											<Link
												data-testid="forgotPasswordLink"
												className="no-underline"
												to={`${buildLocalizeRoute(AppRoutes.ForgotPassword)}`}>
												<span className="underline-link">
													{t('loginForgotPassword')}
												</span>
											</Link>
										</Box>
										<Box className="text-center mt-1 text-[14px]">
											<span>{t('dontHaveAnAccount')}</span>
											<span
												data-testid="signUpLink"
												onClick={() => {
													setShowRegDialog(true);
												}}
												className="underline-link text-primary mx-1">
												{t('signUpInLogin')}
											</span>
										</Box>
										<Box className="flex items-center justify-center gap-2 mt-3">
											<a
												data-event="google_play_button_clicked"
												target="_blank"
												rel="noreferrer"
												href="https://play.google.com/store/apps/details?id=com.fundingsouq"
												onClick={() => trackCustomerIo(OpenPanelEvents.googlePlayButtonClicked, {
													platform: 'android',
													page: 'login',
													region: IS_SA ? 'ksa' : 'uae',
												})}>
												<img className="w-[135px] h-[45px]" src={googlePlayBadge} alt="Get it on Google Play" />
											</a>
											<a
												data-event="app_store_button_clicked"
												target="_blank"
												rel="noreferrer"
												href="https://apps.apple.com/us/app/funding-souq/id6739612948"
												onClick={() => trackCustomerIo(OpenPanelEvents.appStoreButtonClicked, {
													platform: 'ios',
													page: 'login',
													region: IS_SA ? 'ksa' : 'uae',
												})}>
												<img className="w-[135px] h-[45px]" src={appStoreBadge} alt="Download on the App Store" />
											</a>
										</Box>
									</Form>
								);
							}}
						</Formik>
					</Box>
				</Box>
			</div>
			{isUserLockOut ? (
				<UserLockOutDialog
					state={isUserLockOut}
					onClose={() => {
						setIsUserLockOut(false);
					}}></UserLockOutDialog>
			) : null}

			{isShowOtpScreen ? (
				<OTPAuthenticationDialog
					roles={loginResp?.roles}
					type={OTPTypes.Authentication}
					onClose={(isVerified) => {
						setIsShowOtpScreen(false);
						setLoading(false);
						if (isVerified) {
							afterLoginSuccess();
						} else {
							Logout();
						}
					}}
					state={isShowOtpScreen}></OTPAuthenticationDialog>
			) : null}
			{showRegDialog ? (
				<RegistrationDialog
					state={showRegDialog}
					onClose={(e) => {
						setShowRegDialog(false);
					}}
				/>
			) : null}
		</>
	);
}

export default Login;
